import {
  AdminLayout,
  Loading,
  useLocalStorage,
  useSource,
  useTranslation,
  useUrlParam,
} from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { AdminToolbar } from "@opendash/ui";
import { Button, Flex, Select, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { HeaderCards, LogTable } from "../../features/machinelog";
import { MachinelogContextProvider } from "../../features/machinelog/context/MachinelogContext";
import { useExcelExportData } from "../../features/machinelog/hooks/useExcelExportData";
import { MachinelogState } from "../../features/machinelog/states/MachinelogState";
import { useMessages } from "../../features/messages/hooks/useMessages";

export const MachinelogPage = observer(() => {
  const t = useTranslation();
  const machinelogState = useMemo(() => new MachinelogState(), []);
  const [selectedSource] = useSource();
  const [_, setSearchString] = useUrlParam<string>("search", "");
  const [searchValue, setSearchValue] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const [tableView, setTableView] = useLocalStorage<"timeline" | "card">(
    "maintenance:machinelog-view",
    "timeline"
  );
  const { loading, messages } = useMessages({
    id: selectedSource.id,
    classname: Source.className,
    reload: machinelogState.reloadData,
  });
  const { exportTableData } = useExcelExportData();

  useEffect(() => {
    let newsearchstring = searchValue;
    if (filter.length > 0) {
      newsearchstring = newsearchstring.concat(" types:" + filter);
    }
    setSearchString(newsearchstring);
  }, [searchValue, filter]);

  useEffect(() => {
    machinelogState.reload();
  }, [selectedSource]);

  return (
    <MachinelogContextProvider value={machinelogState}>
      <AdminLayout contentPadding>
        <AdminToolbar
          title={
            t("maintenance:machinelog.headline") + " " + selectedSource.name
          }
          onSearch={setSearchValue}
          actions={
            <Flex align="center" gap="16px">
              <Typography.Text>
                {t("maintenance:machinelog.view.label")}:
              </Typography.Text>
              <Select
                value={tableView}
                onChange={setTableView}
                style={{ width: 100 }}
                options={[
                  {
                    label: t("maintenance:machinelog.view.timeline"),
                    value: "timeline",
                  },
                  {
                    label: t("maintenance:machinelog.view.card"),
                    value: "card",
                  },
                ]}
              />
              <Typography.Text>Filter:</Typography.Text>
              <Select
                style={{ width: 200 }}
                options={[
                  {
                    label: t("maintenance:machinelog.filter.schedule"),
                    value: "schedule",
                  },
                  {
                    label: t("maintenance:machinelog.filter.ticket"),
                    value: "ticket",
                  },
                  {
                    label: t("maintenance:machinelog.filter.message"),
                    value: "message",
                  },
                  { label: t("maintenance:machinelog.filter.all"), value: "" },
                ]}
                value={filter}
                onChange={setFilter}
              />
              <Button
                onClick={() => exportTableData(messages ?? [], selectedSource)}
                disabled={loading}
              >
                {t("maintenance:global.button.export_label")}
              </Button>
            </Flex>
          }
        />
        <div className="od-page-main">
          {loading && <Loading message={null} />}
          {!loading && (
            <>
              <HeaderCards />
              <LogTable loading={loading} messages={messages ?? []} />
            </>
          )}
        </div>
      </AdminLayout>
    </MachinelogContextProvider>
  );
});
